import * as React from 'react';
import { CommonContextProvider } from '../features/common/contexts/common-context';
import {
  PicButton,
  PicRowField,
  PicText,
  PicNotifier,
  PicLink,
  PicRouter,
  PicFontSize,
  PicFontWeight,
  PicTag,
  PicColor,
  PicField,
  PicIconButton,
  PicIconName,
} from '@cardinalblue/pic-collage-cms-ui';
import { TemplateApi } from '../features/templates/apis/template-api';
import SingleTemplateContentSection from '../features/templates/components/single-template-contents';
import MultiTemplateContentSection from '../features/templates/components/multi-template-contents';
import { TemplateModel } from '../features/common/models/template-model';
import { JsonDiffView } from '../features/templates/components/json-diff-view';
import { TemplateTagApi } from '../features/template-tags/apis/template-tag-api';
import { TemplateTagModel } from '../features/common/models/template-tag-model';
import { getVipTemplateTagName, restoreTemplateTagsByTemplateId } from '../features/common/utils/template-tag-helpers';
type Props = {
  templateId: number;
};

const ShowTemplatePageContent: React.FC<Props> = ({ templateId }) => {
  const picNotifier = React.useMemo(() => new PicNotifier(), []);
  const picRouter = React.useMemo(() => new PicRouter(), []);

  const templateApi = React.useMemo(() => TemplateApi.create(), []);
  const templateTagApi = React.useMemo(() => TemplateTagApi.create(), []);
  const [template, setTemplate] = React.useState<TemplateModel | null>(null);

  const [templateTags, setTemplateTags] = React.useState<TemplateTagModel[] | null>(null);
  const fetchTemplateTags = React.useCallback(async (): Promise<Error | null> => {
    const [error, returnedTemplateTags] = await templateTagApi.fetchAllTemplateTags();
    if (error) {
      picNotifier.notify({
        type: 'error',
        message: error.message,
      });
      return error;
    }
    setTemplateTags(returnedTemplateTags);
    return null;
  }, [templateTagApi, picNotifier]);

  React.useEffect(() => {
    fetchTemplateTags();
  }, [fetchTemplateTags]);

  const vipTemplateTag = React.useMemo(() => {
    if (!templateTags) {
      return null;
    }
    return getVipTemplateTagName(templateTags);
  }, [templateTags]);

  const fetchTemplate = React.useCallback(async (): Promise<Error | null> => {
    const [error, returnedTemplate] = await templateApi.fetchTemplatebyId(templateId);
    if (error) {
      picNotifier.notify({
        type: 'error',
        message: error.message,
      });
      return error;
    }
    setTemplate(returnedTemplate);
    return null;
  }, [templateApi, picNotifier]);

  React.useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const selectedTemplateTags = React.useMemo(() => {
    if (!template || !templateTags) {
      return [];
    }
    return restoreTemplateTagsByTemplateId(template.getTemplateTagIds(), templateTags);
  }, [template, templateTags]);

  const goToSourceUrl = React.useCallback(() => {
    if (template?.getSourceUrl()) {
      picRouter.open(template.getSourceUrl());
    }
  }, [template]);

  const getTemplateTagEditPage = React.useCallback(
    (templateTagId: number) => `/admin/template_tags/${templateTagId}/edit`,
    [],
  );

  const handleCopyTemplateTagsClick = React.useCallback(() => {
    if (selectedTemplateTags.length === 0) {
      picNotifier.notify({
        type: 'error',
        message: 'No template tags selected',
      });
      return;
    }

    const templateTagsString = selectedTemplateTags.map((tag) => tag.getName()).join(',');
    navigator.clipboard.writeText(templateTagsString);
    picNotifier.notify({
      type: 'success',
      message: 'Template tags copied to clipboard',
    });
  }, [selectedTemplateTags, picNotifier]);

  const handleCopyLabelsClick = React.useCallback(() => {
    const templateLabels = template?.getLabels() ?? [];
    if (templateLabels.length === 0) {
      picNotifier.notify({
        type: 'error',
        message: 'No labels selected',
      });
      return;
    }

    const labelsString = templateLabels.join(',');
    navigator.clipboard.writeText(labelsString);
    picNotifier.notify({
      type: 'success',
      message: 'Labels copied to clipboard',
    });
  }, [template, picNotifier]);

  return (
    <div style={{ display: 'flex', flexFlow: 'column', gap: '36px' }}>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '36px' }}>
        <div style={{ flexBasis: '40%', minWidth: '41.666667%' }}>
          {template && template.getThumbnailUrl() && (
            <img src={template.getThumbnailUrl()} alt="template image" style={{ width: '100%' }} />
          )}
          {template && template.getAnimatedThumbnailUrl() && (
            <>
              <PicText size={PicFontSize.Xl} weight={PicFontWeight.Medium}>
                Animated Thumbnail
              </PicText>
              <img src={template?.getAnimatedThumbnailUrl()} alt="animated image" style={{ width: '100%' }} />
            </>
          )}
        </div>
        <div style={{ display: 'flex', flexFlow: 'column', flexBasis: '60%', gap: '24px', maxWidth: '60%' }}>
          <div style={{ display: 'flex', flexFlow: 'column', gap: '12px' }}>
            <PicText size={PicFontSize.Xl} weight={PicFontWeight.Medium}>
              Template
            </PicText>
            {template && (
              <div style={{ width: 'fit-content' }}>
                <PicButton href={`/templates/${templateId}/edit`} copy="Edit" type="primary" />
              </div>
            )}
            {template && (
              <div style={{ display: 'flex', flexFlow: 'column', gap: '6px' }}>
                <PicRowField label="id" labelWidth="30%">
                  <PicText>{template.getId()}</PicText>
                </PicRowField>
                <PicRowField label="author" labelWidth="30%">
                  <PicText>{template.getAuthor()}</PicText>
                </PicRowField>
                <PicRowField label="uploader_info" labelWidth="30%">
                  <PicText>{template.getUploaderInfo()}</PicText>
                </PicRowField>
                <PicRowField label="source url" labelWidth="30%">
                  <PicLink copy={template.getSourceUrl() ?? ''} onClick={goToSourceUrl} />
                </PicRowField>
                <PicRowField label="created at" labelWidth="30%">
                  <PicText>{template.getCreatedAt().toTimeString()}</PicText>
                </PicRowField>
                <PicRowField label="updated at" labelWidth="30%">
                  <PicText>{template.getUpdatedAt().toTimeString()}</PicText>
                </PicRowField>
                <PicRowField label="adjustable?" labelWidth="30%">
                  <PicText>{template.getIsAdjustable().toString()}</PicText>
                </PicRowField>
                {template.isSingleTemplateModel() && (
                  <PicRowField label="creator id" labelWidth="30%">
                    <PicText>{template.getCreatorId()}</PicText>
                  </PicRowField>
                )}
                <PicRowField label="suitable photos count" labelWidth="30%">
                  <PicText>{template.getFormattedSuitablePhotosCount()}</PicText>
                </PicRowField>
                <PicRowField label="suits video" labelWidth="30%">
                  <PicText>{template.getSuitsVideo().toString()}</PicText>
                </PicRowField>
                <PicRowField label="categories" labelWidth="30%">
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                    <div style={{ flexGrow: 1, display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {selectedTemplateTags.map((tag) => (
                        <a key={tag.getId()} href={getTemplateTagEditPage(tag.getId())}>
                          <PicTag
                            copy={tag.getName()}
                            color={tag.getIsVip() ? PicColor.Yellow500 : PicColor.Teal500}
                            onClick={() => {}}
                          />
                        </a>
                      ))}
                    </div>
                    <PicLink copy="Copy" onClick={handleCopyTemplateTagsClick} />
                  </div>
                </PicRowField>
                <PicRowField label="labels" labelWidth="30%">
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                    <div style={{ flexGrow: 1, display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {template.getLabels().map((label) => (
                        <PicTag key={label} copy={label} color={PicColor.Teal500} />
                      ))}
                    </div>
                    <PicLink copy="Copy" onClick={handleCopyLabelsClick} />
                  </div>
                </PicRowField>
                <PicRowField label="minimal version iOS" labelWidth="30%">
                  <PicText>{template.getMinimalRequiredVersionIos()?.toString()}</PicText>
                </PicRowField>
                <PicRowField label="minimal version Android" labelWidth="30%">
                  <PicText>{template.getMinimalRequiredVersionAndroid()?.toString()}</PicText>
                </PicRowField>
              </div>
            )}
          </div>
          <div>{template?.isSingleTemplateModel() && <SingleTemplateContentSection template={template} />}</div>
          <div>{template?.isMultiTemplateModel() && <MultiTemplateContentSection template={template} />}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {template && template.isSingleTemplateModel() && (
          <div style={{ display: 'flex', flexFlow: 'column', gap: '24px' }}>
            <PicField label="Collage Structure">
              <JsonDiffView oldJson={template.getCollageStruct()} newJson={template.getCollageStruct()} />
            </PicField>
          </div>
        )}
      </div>
    </div>
  );
};

const ShowTemplatePage: React.FC<Props> = (props) => {
  return (
    <CommonContextProvider>
      <ShowTemplatePageContent {...props} />
    </CommonContextProvider>
  );
};

export default ShowTemplatePage;
